import { FC, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { signOut } from '../../lib/auth';
import ProfileInfo from './ProfileInfo';
import PhotoUpload from './PhotoUpload';
import { supabase } from '../../lib/supabase';

const Profile: FC = () => {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [gcashNumber, setGcashNumber] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createdDate = user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-';

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      try {
        // Load user profile data
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('display_name')
          .eq('user_id', user.id)
          .single();

        // Load GCash number
        const { data: gcashData } = await supabase
          .from('gcash_numbers')
          .select('mobile_number')
          .eq('user_id', user.id)
          .maybeSingle();

        // Load photo data from profile_photos table
        const { data: photoData } = await supabase
          .from('profile_photos')
          .select('photo_url')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileData) {
          setDisplayName(profileData.display_name);
        }
        if (gcashData) {
          setGcashNumber(gcashData.mobile_number);
        }
        if (photoData) {
          setPhotoUrl(photoData.photo_url);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };
    loadProfile();
  }, [user]);

  const handlePhotoUpdate = async (url: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profile_photos')
        .upsert({
          user_id: user.id,
          photo_url: url
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setPhotoUrl(url);
    } catch (error) {
      console.error('Failed to update profile photo:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      // Validate GCash number format
      if (gcashNumber && !/^09[0-9]{9}$/.test(gcashNumber)) {
        throw new Error('Invalid GCash number format. Must start with 09 and be 11 digits long.');
      }

      if (gcashNumber) {
        const { error: gcashError } = await supabase
          .from('gcash_numbers')
          .upsert({
            user_id: user.id,
            mobile_number: gcashNumber
          }, {
            onConflict: 'user_id'
          });

        if (gcashError) throw gcashError;
      } else {
        // If gcashNumber is empty, delete the record
        const { error: deleteError } = await supabase
          .from('gcash_numbers')
          .delete()
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;
      }

      setEditing(false);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-6">
          {user && (
            <PhotoUpload
              userId={user.id}
              currentPhotoUrl={photoUrl}
              onPhotoUpdate={handlePhotoUpdate}
            />
          )}

          <div className="space-y-4 mt-6">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">Display Name</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md text-gray-900">
                {displayName || 'No display name set'}
              </div>
            </div>

            {editing ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">GCash Mobile Number</label>
                  <input
                    type="tel"
                    value={gcashNumber}
                    onChange={(e) => setGcashNumber(e.target.value)}
                    placeholder="09XX XXX XXXX"
                    pattern="^09[0-9]{9}$"
                    maxLength={11}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <p className="mt-1 text-sm text-gray-500">Format: 09XXXXXXXXX</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    disabled={loading}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">GCash Mobile Number</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md text-gray-900">
                    {gcashNumber || 'No GCash number set'}
                  </div>
                </div>
                <button
                  onClick={() => setEditing(true)}
                  className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Edit GCash Number
                </button>
              </>
            )}

            <ProfileInfo label="Email" value={user?.email || '-'} />
            <ProfileInfo label="Account Created" value={createdDate} />
          </div>

          <button
            onClick={signOut}
            className="w-full mt-8 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;